import { FGStack } from '@fg/design-system';
import FeedEmptyState from 'components/FeedEmptyState';
import React, { useCallback, useMemo } from 'react';
import { IActiveCountry } from '../../../interfaces';
import { isEmpty, keyBy } from 'lodash/fp';
import GQLoader from 'components/GQLoader/GQLoader';
import FeedHeader from 'components/eventsFeed/FeedHeader';
import DateSeparator from 'components/eventsFeed/EventsFeedDateSeparator';
import InsightItem, { InsightWithDateString } from './InsightItem';
import { InsightMenuProps } from './InsightMenu';
import FullHeightScroll from 'components/FullHeightScroll';

type Props = {
    insights: InsightWithDateString[];
    isLoading: boolean;
    loadFailed?: boolean;
    loadMore: () => void;
    licensedCountriesArr: IActiveCountry[];
    isShowCountryFilter?: boolean;
    isShowDateFilter?: boolean;
    onInsightClick?: (id: string) => void;
    maxDate: moment.Moment;
    minDate: moment.Moment;
    selectedCountries: number[];
    searchString: string;
    onSetSearchString: (value: string) => void;
    removeCountry: (countryId: number) => void;
    addCountry: (countryId: number) => void;
    toggleCountry: (countryId: number) => void;
    onDateRangeChange: (startDate: Date, endDate: Date) => void;
    onDateRangeReset: () => void;
    activeId?: string;
    isGQInternalUser?: boolean;
} & Omit<InsightMenuProps, 'insight'>;

const Insights = React.memo((props: Props) => {
    //todo: DRY and add CountryById type to interfaces.ts
    const countriesById: { [countryID: number]: IActiveCountry } = useMemo(
        () =>
            keyBy(
                (country: IActiveCountry) => country.id,
                props.licensedCountriesArr
            ),
        [props.licensedCountriesArr]
    );

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (scrollHeight - scrollTop - clientHeight < 200) {
                props.loadMore();
            }
        },
        [props.loadMore]
    );

    return (
        <FGStack
            style={{ height: '100%', width: '100%' }}
            direction="column"
            gap={4}>
            <FeedHeader
                isShowCountryFilter
                isShowDateFilter
                maxDate={props.maxDate}
                minDate={props.minDate}
                selectedCountries={props.selectedCountries}
                searchString={props.searchString}
                onSetSearchString={props.onSetSearchString}
                removeCountry={props.removeCountry}
                addCountry={props.addCountry}
                toggleCountry={props.toggleCountry}
                onDateRangeChange={props.onDateRangeChange}
                onDateRangeReset={props.onDateRangeReset}
            />
            <FeedEmptyState
                isLoading={props.isLoading}
                loadFailed={props.loadFailed}
                isEmpty={isEmpty(props.insights)}
                failedMessage="Couldn't load insights"
            />
            <FullHeightScroll onScroll={handleScroll}>
                {props.insights.map((insight, index) => {
                    const previousInsight = props.insights[index - 1];
                    const shouldDrawSeparator =
                        previousInsight &&
                        index > 0 &&
                        insight.dateString !== previousInsight.dateString;
                    return (
                        <React.Fragment key={insight._id}>
                            {shouldDrawSeparator && (
                                <DateSeparator caption={insight.dateString} />
                            )}
                            <InsightItem
                                insight={insight}
                                countriesById={countriesById}
                                onClick={props.onInsightClick}
                                isActive={props.activeId === insight._id}
                                canShare={props.canShare}
                                canDownloadData={props.canDownloadData}
                                onEdit={props.onEdit}
                                onBroadcast={props.onBroadcast}
                                onDownload={props.onDownload}
                                onDelete={props.onDelete}
                                isGQInternalUser={props.isGQInternalUser}
                            />
                        </React.Fragment>
                    );
                })}
            </FullHeightScroll>
            {props.isLoading && !isEmpty(props.insights) && <GQLoader />}
        </FGStack>
    );
});

export default Insights;
